import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  TutorVersion,
  activateVersion,
  deleteVersion,
  getVersions,
} from '../../../services/tutorStructureService';

interface VersionPanelProps {
  courseId: number;
  refreshKey?: number;
}

export default function VersionPanel({ courseId, refreshKey }: VersionPanelProps) {
  const [versions, setVersions] = useState<TutorVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [toastSeverity, setToastSeverity] = useState<'error' | 'success'>('success');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [actionPending, setActionPending] = useState<number | null>(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getVersions(courseId);
      setVersions(data);
    } catch {
      setError('Failed to load versions.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    loadVersions();
  }, [loadVersions, refreshKey]);

  const handleActivate = async (versionId: number) => {
    setActionPending(versionId);
    try {
      await activateVersion(courseId, versionId);
      setToastSeverity('success');
      setToast('Version set as active — students will see this version.');
      await loadVersions();
    } catch {
      setToastSeverity('error');
      setToast('Failed to activate version. Please try again.');
    } finally {
      setActionPending(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId === null) return;
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    setActionPending(id);
    try {
      await deleteVersion(courseId, id);
      setToastSeverity('success');
      setToast('Version deleted.');
      await loadVersions();
    } catch (err: any) {
      setToastSeverity('error');
      if (err?.response?.status === 409) {
        setToast('Cannot delete the active version — promote a different version first.');
      } else {
        setToast('Failed to delete version. Please try again.');
      }
    } finally {
      setActionPending(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Published Versions
      </Typography>
      <Divider sx={{ mb: 1 }} />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
      )}

      {!loading && !error && versions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No versions yet — publish your first version using the button above.
        </Typography>
      )}

      {!loading && versions.length > 0 && (
        <List dense disablePadding>
          {versions.map((v) => (
            <ListItem
              key={v.id}
              disableGutters
              sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <ListItemText
                  primary={v.versionLabel}
                  secondary={formatDate(v.createdAt)}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                  sx={{ flex: 1, m: 0 }}
                />
                <Chip
                  label={v.isActive ? 'Active' : 'Inactive'}
                  color={v.isActive ? 'success' : 'default'}
                  size="small"
                />
                {!v.isActive && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={actionPending === v.id}
                    onClick={() => handleActivate(v.id)}
                  >
                    Set Active
                  </Button>
                )}
                {!v.isActive && (
                  <Button
                    size="small"
                    color="error"
                    disabled={actionPending === v.id}
                    onClick={() => setDeleteConfirmId(v.id)}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              <Divider sx={{ width: '100%', mt: 0.5 }} />
            </ListItem>
          ))}
        </List>
      )}

      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete version?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the version and its snapshot. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toastSeverity} onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
}
